import React, { FC } from 'react';

import { Box, Center, HStack } from '@chakra-ui/react';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { DownIcon, RightIcon } from 'components/Icons';
import { useResourceType } from 'containers/App/hooks/useResourceType';

import { DataAccessRow } from './utils';

// How far each level of the tree is indented.
const INDENT_PX = 20;

interface DataNameCellProps {
  row: DataAccessRow;

  onToggle(row: DataAccessRow): void;
}

/**
 * The resource's name and type, indented by its level, with a chevron that
 * opens it: a database shows its schemas, a schema its tables, and a table
 * its details.
 */
export const DataNameCell: FC<DataNameCellProps> = ({ row, onToggle }) => {
  const { getCloudNativeName } = useResourceType();
  return (
    <HStack spacing={2} pl={`${row.depth * INDENT_PX}px`}>
      <Box flexShrink={0} minW={5}>
        {row.canOpen && (
          <Center
            as="button"
            type="button"
            aria-label={`${row.isOpen ? 'Close' : 'Open'} ${row.name}`}
            aria-expanded={row.isOpen}
            boxSize={5}
            borderRadius={4}
            color="gray.500"
            _hover={{ bg: 'gray.50', color: 'primary' }}
            onClick={e => {
              e.stopPropagation();
              onToggle(row);
            }}
          >
            {row.isOpen ? <DownIcon boxSize={3} /> : <RightIcon boxSize={3} />}
          </Center>
        )}
      </Box>
      <WithResourceIcon
        resourceType={row.type}
        hStackStyles={{ flex: 1, minW: 0 }}
      >
        <StackedCell upper={row.name} lower={getCloudNativeName(row.type)} />
      </WithResourceIcon>
    </HStack>
  );
};
